import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { MapPin, Phone, Mail } from 'lucide-react';
import CondividiStudio from './CondividiStudio';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '../styles/map-clusters.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix per i marker di Leaflet
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Icona DEFAULT - marker standard Leaflet
const defaultIcon = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Icona PIN - goccia verde moderna
const pinIcon = new L.DivIcon({
  html: `<div style="
    background: linear-gradient(135deg, #10b981, #059669);
    width: 24px;
    height: 24px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 3px solid white;
    box-shadow: 0 3px 6px rgba(0,0,0,0.3);
  "></div>`,
  className: 'pin-marker',
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24]
});

// Icona CIRCLE - cerchio verde
const circleIcon = new L.DivIcon({
  html: `<div style="
    background: linear-gradient(135deg, #10b981, #059669);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  className: 'circle-marker',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -8]
});

// === ICONE SEDI SECONDARIE (BLU) ===

// Icona secondaria DEFAULT - marker Leaflet blu (usa classe CSS)
const secondaryDefaultIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [20, 33],
  iconAnchor: [10, 33],
  popupAnchor: [1, -28],
  shadowSize: [33, 33]
});

// Icona secondaria PIN - goccia blu
const secondaryPinIcon = new L.DivIcon({
  html: `<div style="
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    width: 20px;
    height: 20px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  className: 'secondary-pin-marker',
  iconSize: [20, 20],
  iconAnchor: [10, 20],
  popupAnchor: [0, -20]
});

// Icona secondaria CIRCLE - cerchio blu
const secondaryCircleIcon = new L.DivIcon({
  html: `<div style="
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  className: 'secondary-circle-marker',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -7]
});

// Funzione per ottenere l'icona in base al tipo (primarie - verdi)
const getPrimaryIcon = (iconType: string) => {
  switch (iconType) {
    case 'pin': return pinIcon;
    case 'circle': return circleIcon;
    default: return defaultIcon;
  }
};

// Funzione per ottenere l'icona secondaria in base al tipo (blu)
const getSecondaryIcon = (iconType: string) => {
  switch (iconType) {
    case 'pin': return secondaryPinIcon;
    case 'circle': return secondaryCircleIcon;
    default: return secondaryDefaultIcon;
  }
};

interface CertifiedActivity {
  name: string;
  isCertified: 'SI' | 'NO' | 'PREAUTORIZZATO';
}

export interface MemberLocation {
  id: string;
  businessName: string;
  address: string;
  province: string;
  region: string;
  email?: string;
  phone?: string;
  currentCertificateStatus?: string;
  certifiedActivities?: CertifiedActivity[];
  coordinates: {
    lat: number;
    lng: number;
  };
  isSecondarySede?: boolean;  // Flag per sedi secondarie
}

// Tipi di icona marker disponibili
export type MarkerIconType = 'default' | 'pin' | 'circle';

// Impostazioni mappa configurabili
export interface MapSettings {
  enableClustering: boolean;
  defaultZoom: number;
  markerIconType: MarkerIconType;
  secondaryMarkerIconType: MarkerIconType;  // Tipo icona per sedi secondarie
  sameMarkerForAll: boolean;  // Se true, usa stesso marker per sedi principali e secondarie
  clusterRadius: number;
  showMemberCount: boolean;  // Se false, nasconde il contatore agli utenti non loggati
  filterNauticaByIntegrativa: boolean;  // Filtra STED solo per chi ha integrativa NAUTICA
}

// Default settings
export const DEFAULT_MAP_SETTINGS: MapSettings = {
  enableClustering: false,  // Default: clustering disabilitato
  defaultZoom: 6,
  markerIconType: 'default',
  secondaryMarkerIconType: 'circle',  // Default: cerchio blu per sedi secondarie
  sameMarkerForAll: false,  // Default: marker diversi per sedi secondarie
  clusterRadius: 80,
  showMemberCount: true,  // Default: mostra contatore a tutti
  filterNauticaByIntegrativa: false  // Default: mostra STED a tutti
};

interface MapWrapperProps {
  members: MemberLocation[];
  center: [number, number];
  zoom: number;
  settings?: MapSettings;
  fitToMarkers?: boolean;  // Se true, adatta la mappa ai marker visibili
  onMemberSelect?: (member: MemberLocation) => void;  // Callback per selezione membro (usato dal wizard collaborazioni)
  enableShare?: boolean;  // Bottoni «Copia» e «WhatsApp» nel popup (pagina pubblica)
  focusMemberId?: string | null;  // Centra la mappa su questo studio e apre la sua scheda (link ?studio=<id>)
  height?: string;  // Altezza del contenitore Leaflet (default 600px; '100%' per adattarsi al genitore)
}

/**
 * Componente interno: centra la mappa sullo studio richiesto e apre il suo popup.
 */
const FocusMember: React.FC<{
  members: MemberLocation[];
  focusMemberId?: string | null;
  markerRefs: React.MutableRefObject<Map<string, L.Marker>>;
}> = ({ members, focusMemberId, markerRefs }) => {
  const map = useMap();

  React.useEffect(() => {
    if (!focusMemberId) return;
    const member = members.find(m => m.id === focusMemberId);
    if (!member) return;
    map.setView([member.coordinates.lat, member.coordinates.lng], 15, { animate: false });
    // Il marker viene montato dopo la mappa: si apre il popup al giro successivo
    const t = window.setTimeout(() => {
      markerRefs.current.get(focusMemberId)?.openPopup();
    }, 50);
    return () => window.clearTimeout(t);
  }, [focusMemberId, members, map, markerRefs]);

  return null;
};

/**
 * Componente interno per gestire fitBounds sui marker
 */
const FitBoundsToMarkers: React.FC<{ members: MemberLocation[] }> = ({ members }) => {
  const map = useMap();

  React.useEffect(() => {
    if (members.length === 0) return;

    // Calcola i bounds di tutti i marker
    const bounds = L.latLngBounds(
      members.map(m => [m.coordinates.lat, m.coordinates.lng] as [number, number])
    );

    // Adatta la mappa con padding e animazione
    map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 12,  // Non zoomare troppo
      animate: true,
      duration: 0.5
    });
  }, [members, map]);

  return null;
}

/**
 * Funzione per creare icona cluster personalizzata
 */
const createClusterCustomIcon = (cluster: any) => {
  const count = cluster.getChildCount();
  let size = 'small';

  if (count > 50) {
    size = 'large';
  } else if (count > 10) {
    size = 'medium';
  }

  return L.divIcon({
    html: `<div class="cluster-icon cluster-${size}"><span>${count}</span></div>`,
    className: 'custom-marker-cluster',
    iconSize: L.point(40, 40, true),
  });
};

/**
 * Wrapper component per la mappa Leaflet
 * Separato per gestire meglio la compatibilità con React 19
 * Supporta clustering opzionale e diversi tipi di icone
 */
const MapWrapper: React.FC<MapWrapperProps> = ({ members, center, zoom, settings = DEFAULT_MAP_SETTINGS, fitToMarkers = false, onMemberSelect, enableShare = false, focusMemberId = null, height = '600px' }) => {
  const { enableClustering, markerIconType, secondaryMarkerIconType, sameMarkerForAll, clusterRadius } = settings;
  const markerRefs = React.useRef<Map<string, L.Marker>>(new Map());

  // Funzione per ottenere l'icona corretta
  const getIcon = (isSecondary: boolean) => {
    // Se sameMarkerForAll è true, usa sempre il marker principale (verde)
    if (sameMarkerForAll || !isSecondary) {
      return getPrimaryIcon(markerIconType);
    }
    return getSecondaryIcon(secondaryMarkerIconType);
  };

  // Renderizza i marker
  const renderMarkers = () => (
    <>
      {members.map((member) => (
        <Marker
          key={member.id}
          position={[member.coordinates.lat, member.coordinates.lng]}
          icon={getIcon(member.isSecondarySede || false)}
          ref={(m) => {
            if (m) markerRefs.current.set(member.id, m);
            else markerRefs.current.delete(member.id);
          }}
        >
          <Popup maxWidth={300} minWidth={240}>
            <div className="p-2">
              {/* Badge sede secondaria */}
              {member.isSecondarySede && (
                <div className="mb-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    📍 Sede Secondaria
                  </span>
                </div>
              )}

              <h3 className="font-bold text-base text-gray-900 mb-2 leading-snug">
                {member.businessName}
              </h3>

              <div className="space-y-2 text-sm">
                {member.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{member.address}</span>
                  </div>
                )}

                {member.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <a href={`tel:${member.phone}`} className="text-blue-600 hover:underline">
                      {member.phone}
                    </a>
                  </div>
                )}

                {member.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <a href={`mailto:${member.email}`} className="text-purple-600 hover:underline break-all">
                      {member.email}
                    </a>
                  </div>
                )}
              </div>

              {/* Attività Certificate/Preautorizzate */}
              {member.certifiedActivities && member.certifiedActivities.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs font-semibold text-gray-700 mb-1.5">Attività Certificate:</div>
                  <div className="flex flex-wrap gap-1">
                    {member.certifiedActivities.map((activity, idx) => (
                      <span
                        key={idx}
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          activity.isCertified === 'SI' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}
                        title={activity.name}
                      >
                        {activity.isCertified === 'SI' ? '✓' : '⏳'} {activity.name.length > 25 ? activity.name.substring(0, 25) + '...' : activity.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-200">
                {member.currentCertificateStatus === 'ATTIVO' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Certificato ATTIVO
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Membro Rete Certificata
                  </span>
                )}
              </div>

              {enableShare && (
                <CondividiStudio studio={member} variant="popup" className="mt-3" />
              )}

              {onMemberSelect && (
                <button
                  onClick={(e) => { e.stopPropagation(); onMemberSelect(member); }}
                  className="mt-3 w-full py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Seleziona questo studio
                </button>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      style={{ height, width: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* Adatta automaticamente la mappa ai marker se richiesto (non se c'e' uno studio da mettere a fuoco) */}
      {fitToMarkers && !focusMemberId && members.length > 0 && (
        <FitBoundsToMarkers members={members} />
      )}

      {focusMemberId && (
        <FocusMember members={members} focusMemberId={focusMemberId} markerRefs={markerRefs} />
      )}

      {enableClustering ? (
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          maxClusterRadius={clusterRadius}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
        >
          {renderMarkers()}
        </MarkerClusterGroup>
      ) : (
        renderMarkers()
      )}
    </MapContainer>
  );
};

export default MapWrapper;

